import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { Activity, Bot, Clock, Target } from 'lucide-react';
import authService from '../../services/authService';

export const MyAgents = () => {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUserAgents = async () => {
            try {
                const user = authService.getCurrentUser();
                if (!user) {
                    setLoading(false);
                    return;
                }

                // Fetch agents created by this user
                const q = query(
                    collection(db, 'agents'),
                    where('firebaseUid', '==', user.uid)
                );

                const querySnapshot = await getDocs(q);
                const userAgents = [];

                for (const doc of querySnapshot.docs) {
                    const agentData = { id: doc.id, ...doc.data() };

                    // Fetch last 3-5 activities for this agent
                    const activitiesQuery = query(
                        collection(db, 'predictions'),
                        where('agentId', '==', agentData.id),
                        orderBy('timestamp', 'desc'),
                        limit(5)
                    );

                    const activitiesSnapshot = await getDocs(activitiesQuery);
                    agentData.recentActivities = activitiesSnapshot.docs.map(aDoc => ({
                        id: aDoc.id,
                        ...aDoc.data()
                    }));

                    userAgents.push(agentData);
                }

                setAgents(userAgents);
            } catch (error) {
                console.error("Error fetching user agents:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserAgents();
    }, []);

    if (loading) {
        return <div className="animate-pulse text-muted-foreground p-6 bg-[#1A1A1A] rounded-2xl border border-white/10">Loading your AI Agents...</div>;
    }

    if (agents.length === 0) {
        return (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 text-center">
                <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-bold text-white mb-2">No AI Agents Yet</h3>
                <p className="text-muted-foreground text-sm mb-4">You haven't created any AI agents. Head to the Agent Studio to deploy your first agent.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Bot className="w-6 h-6 text-primary" />
                My AI Agents
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {agents.map(agent => (
                    <div key={agent.id} className="bg-[#1A1A1A] border border-white/10 rounded-2xl overflow-hidden group">
                        {/* Agent Header */}
                        <div className="p-5 border-b border-white/5 bg-black/20">
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center border border-primary/30">
                                        <Bot className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white text-lg leading-tight">{agent.name}</h4>
                                        <span className="text-xs font-mono text-muted-foreground">{agent.modelType || 'Custom Model'}</span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-emerald-400 font-mono">
                                        {agent.brierScore ? agent.brierScore.toFixed(3) : '--'}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Brier Score</div>
                                </div>
                            </div>

                            <div className="flex gap-4 mt-3">
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <Target className="w-3.5 h-3.5" />
                                    <span>{agent.totalPredictions || 0} Predictions</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <Clock className="w-3.5 h-3.5" />
                                    <span>Rank #{agent.rank || '--'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Recent Activities */}
                        <div className="p-5">
                            <h5 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-2">
                                <Activity className="w-3.5 h-3.5" />
                                Recent Activity
                            </h5>

                            <div className="space-y-3">
                                {agent.recentActivities && agent.recentActivities.length > 0 ? (
                                    agent.recentActivities.map((activity, idx) => {
                                        const pYes = activity.pYes || activity.probability || 0.5;
                                        const side = pYes >= 0.5 ? 'YES' : 'NO';
                                        const confidence = pYes >= 0.5 ? pYes * 100 : (1 - pYes) * 100;

                                        return (
                                            <div key={idx} className="flex justify-between items-start text-sm border-l-2 border-white/10 pl-3 py-1">
                                                <div>
                                                    <div className="text-gray-300 truncate max-w-[200px]" title={activity.marketSlug}>
                                                        {activity.marketSlug.split('-').join(' ')}
                                                    </div>
                                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                                        {new Date(activity.timestamp).toLocaleString()}
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <span className={`font-bold text-xs ${side === 'YES' ? 'text-emerald-400' : 'text-red-400'}`}>
                                                        {side}
                                                    </span>
                                                    <div className="text-[10px] font-mono text-muted-foreground">
                                                        {confidence.toFixed(1)}% conf
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-sm text-muted-foreground text-center py-4 italic">
                                        No recent predictions.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
